/**
 * «Запостить в тестовый канал» (docs/release-2026-09-19.md §9.1) — всё, что не рисует:
 * словарь состояний, разбор ответов и слежение за отправкой до исхода.
 *
 * ПОЧЕМУ ЭТО НЕ ОДОБРЕНИЕ. Одобрение в тестовый канал ПОТРЕБЛЯЕТ черновик: версия уходит в
 * `published`, слот держится навсегда, и боевой ключ того же черновика потом отвергается.
 * Здесь — отдельное действие ключа (`preview`, код 3): пост уходит в тестовый канал ровно в
 * том виде, как выйдет в боевой, а версия, её статус и кнопка «Утвердить…» не меняются вовсе.
 * Поэтому ни одно состояние этого файла не трогает `busy` полосы решения.
 *
 * СЛЕЖЕНИЕ ЖИВЁТ В ПОЛОСЕ, А НЕ В ДИАЛОГЕ. Закрыл диалог посреди отправки — мета-строка «в
 * тесте · отправляется…» обязана дойти до «открыть» сама, а открытый снова диалог — показать
 * ту же отправку, а не предложить вторую. Опрос — самопланирующийся `setTimeout` от последнего
 * ответа, как у `RunStatusBar`: `GET` модели рендера раз в 2 с, пока отправка не закрыта.
 *
 * РАЗБОР ПО ПОЛЯМ, А НЕ СТРОГОЙ СХЕМОЙ ЦЕЛИКОМ. Отправка уже записана сервером, и строгий
 * разбор ответа, споткнувшийся о лишнее поле, сказал бы «не вышло» про то, что вышло. Каждая
 * строка `test_sends` проверяется своей схемой (`TestSendSchema`) — чужая строка выпадает, а
 * не роняет опрос.
 *
 * БРЕНД В ТЕЛЕ — БРЕНД ЭКРАНА (2026-10-25, изоляция брендов): `expected_brand` — бренд адреса,
 * в шапке и теме которого открыта полоса (`expectedBrand`), и бэкенд отказывает
 * `brand_mismatch`, если черновик не того бренда. После перехода `DraftPanel` бренд экрана равен
 * `model.brand`; отправь полоса `model.brand`, сверка сравнивала бы строку черновика саму с
 * собой. Канала в теле нет: превью уходит только в тестовый канал бренда черновика, выбирать
 * человеку нечего.
 */

import { type DraftState, isoToMs, nowMs } from '@smm/contracts';
import {
  BRAND_MISMATCH_REASON,
  type CreateTestSendBody,
  SIGNER_DRAFT_BRAND_PINNED_REASON,
} from '@smm/contracts/schemas';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiRequestError, get, post } from '../../api/client.ts';
import {
  CreateTestSendResponseSchema,
  type TestSend,
  TestSendSchema,
  type TestSendState,
} from '../../api/types.ts';
import { detailIssues } from '../versionSave.ts';
import { signerPinnedText } from './phrases.ts';

/** Статусы версии, у которых есть кнопка (§9.1; тот же набор проверяет бэкенд). */
export const TEST_SEND_STATUSES: ReadonlySet<DraftState> = new Set([
  'pending_review',
  'approved',
  'deferred',
]);

/** Опрос хода — раз в 2 с (§9.1): публикатор забирает превью своим тиком, это секунды. */
export const TEST_SEND_POLL_MS = 2000;

/**
 * Окно ключа превью — ровно 300 с (`SLOT_DEADLINE_S`, §9.1). Отправка, не забранная
 * публикатором за это время, уже не уйдёт: ключ истёк.
 */
export const TEST_SEND_KEY_WINDOW_MS = 300_000;

/**
 * Дольше этого за отправкой не следят: окно ключа (5 мин) плюс уборка выданной, но не
 * отчитанной (`leased` дольше 10 минут → `failed`, §9.1). Отправка, не закрытая и за это
 * время, уже не закроется опросом — её закроет только сервер, а экран перестаёт спрашивать
 * раз в 2 с о том, что не меняется.
 */
export const TEST_SEND_TRACK_MAX_MS = 15 * 60_000;

export const isTerminal = (s: TestSendState): boolean =>
  s === 'sent' || s === 'failed' || s === 'refused';

/**
 * Незакрытая отправка, которая уже не сдвинется сама: ключ не забран за своё окно или
 * публикатор молчит дольше уборки. Показывается «нет ответа», а не вечное «отправляется…».
 */
export function isStalled(t: TestSend, now: number): boolean {
  if (isTerminal(t.state)) return false;
  const age = now - isoToMs(t.created_at);
  return t.state === 'signed' ? age > TEST_SEND_KEY_WINDOW_MS : age > TEST_SEND_TRACK_MAX_MS;
}

/** Ход отправки словами — шаги диалога и мета-строки. */
export const TEST_SEND_STATE_WORD: Readonly<Record<TestSendState, string>> = {
  signed: 'ключ подписан, ждём публикатора',
  leased: 'публикатор отправляет',
  sent: 'отправлено',
  failed: 'не вышло',
  refused: 'публикатор отказал',
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;
}

/** Строки `test_sends` из ответа модели рендера — каждая своей схемой (шапка файла). */
export function testSendsOf(raw: unknown): TestSend[] {
  const list = asRecord(raw)?.['test_sends'];
  if (!Array.isArray(list)) return [];
  const out: TestSend[] = [];
  for (const item of list as readonly unknown[]) {
    const r = TestSendSchema.safeParse(item);
    if (r.success) out.push(r.data);
  }
  return out;
}

/** Отправка из ответа POST: 201 — новая, 200 — уже незакрытая. Не разобралась — `null`. */
export function testSendOfResponse(raw: unknown): TestSend | null {
  const strict = CreateTestSendResponseSchema.safeParse(raw);
  if (strict.success) return strict.data.test_send;
  const r = TestSendSchema.safeParse(asRecord(raw)?.['test_send']);
  return r.success ? r.data : null;
}

/**
 * Свежая из двух. Моменты — ISO_UTC_MS фиксированной ширины, поэтому сравнение строк и есть
 * сравнение моментов; при равенстве решает UUIDv7 (он растёт со временем). Одна и та же
 * отправка — берётся `b`: вызывающий кладёт туда более свежий снимок.
 */
export function newerSend(a: TestSend | null, b: TestSend | null): TestSend | null {
  if (a === null) return b;
  if (b === null) return a;
  if (a.id === b.id) return b;
  if (a.created_at !== b.created_at) return a.created_at > b.created_at ? a : b;
  return a.id > b.id ? a : b;
}

/** Два снимка одной отправки говорят одно и то же: состояние, исход, момент и пост в канале. */
export function sameSnapshot(a: TestSend, b: TestSend): boolean {
  return (
    a.id === b.id &&
    a.state === b.state &&
    a.outcome_code === b.outcome_code &&
    a.outcome_detail === b.outcome_detail &&
    a.finished_at === b.finished_at &&
    a.telegram_message_id === b.telegram_message_id &&
    a.link === b.link
  );
}

/**
 * Пост ушёл, но публикатор сверил ответ Telegram с подписанной формой и нашёл расхождение
 * (`sent_with_deviation`, `services/backend/src/domain/test-send.ts`). Ради этого сигнала
 * тестовая отправка и нужна: в боевом канале пост выглядел бы так же.
 */
export const deviated = (t: TestSend): boolean =>
  t.state === 'sent' && t.outcome_code === 'sent_with_deviation';

/** Расхождение словами; чем именно разошлось — кодами сверки, если бэкенд их назвал. */
export function deviationText(t: TestSend): string {
  const base = 'Telegram показал пост не так, как он подписан';
  return t.outcome_detail === null ? base : `${base} (${t.outcome_detail})`;
}

/** Причина отказа отправки — словами публикатора или уборки; кода нет — честно «не названа». */
export function outcomeText(t: TestSend): string {
  return t.outcome_detail ?? t.outcome_code ?? 'причина не названа';
}

const reasonOf = (e: ApiRequestError): string | null => {
  const r = asRecord(e.details)?.['reason'];
  return typeof r === 'string' ? r : null;
};

export interface TestSendFailure {
  readonly text: string;
  /** Экран устарел — перечитать модель, иначе следующая попытка отобьётся так же. */
  readonly refresh: boolean;
}

/**
 * Отказ POST — словами. Коды повторяют одобрение (`domain/approve.ts`): тестовая отправка
 * проходит те же проверки содержания, и отказы у них одни.
 */
export function testSendErrorText(e: unknown): TestSendFailure {
  if (!(e instanceof ApiRequestError)) {
    return { text: e instanceof Error ? e.message : String(e), refresh: false };
  }
  const reason = reasonOf(e);
  if (e.code === 'channel_not_configured' || reason === 'channel_not_configured') {
    return {
      text:
        'Тестовый канал бренда не задан — отправлять некуда. Его добавляют в конфиг бренда ' +
        '(раздел channels.telegram).',
      refresh: false,
    };
  }
  if (reason === 'content_changed') {
    return {
      text: 'Черновик изменился с момента показа: экран обновлён — проверьте пост и отправьте заново.',
      refresh: true,
    };
  }
  if (reason === 'visual_unresolved') {
    return {
      text: 'По картинке нет решения: примите или отклоните её — тогда пост можно отправить.',
      refresh: true,
    };
  }
  if (reason === 'test_send_in_flight') {
    // Второе нажатие, пока первое ещё подписывается у демона: отправка одна, и её ход
    // придёт с перечитанной моделью.
    return {
      text: 'Отправка этой версии уже подписывается — её ход появится через пару секунд.',
      refresh: true,
    };
  }
  if (reason === SIGNER_DRAFT_BRAND_PINNED_REASON) {
    // Обновление экрана отказ не снимет — перечитывать модель незачем.
    return { text: signerPinnedText(e.message, 'Отправка не подписана'), refresh: false };
  }
  if (reason === BRAND_MISMATCH_REASON) {
    return {
      text: `${e.message}. Отправка не подписана: пост принадлежит не тому бренду, что показан на экране. Экран обновлён.`,
      refresh: true,
    };
  }
  if (reason === 'stale_base_version') {
    return {
      text: 'Эта версия уже не текущая: в тестовый канал уходит текущая. Экран обновлён.',
      refresh: true,
    };
  }
  if (e.code === 'invalid_for_telegram') {
    const list = detailIssues(e.details);
    return {
      text:
        list.length === 0
          ? `Пост не проходит проверки Telegram: ${e.message}.`
          : `Пост не проходит проверки Telegram: ${list.join('; ')}.`,
      refresh: false,
    };
  }
  if (e.code === 'state_conflict') {
    return { text: `${e.message}. Экран обновлён.`, refresh: true };
  }
  if (e.code === 'network_error') return { text: e.message, refresh: false };
  return { text: `${e.message} (${e.code}).`, refresh: false };
}

export interface TestSendTracker {
  /**
   * Отправка, за которой следит ЭТОТ экран: начатая им или незакрытая из модели. Закрытая
   * остаётся здесь, пока диалог показывает её исход (`forget` — вернуться к удержанию).
   */
  readonly tracked: TestSend | null;
  /** Самая свежая отправка версии — из модели или из слежения: её показывает мета-строка. */
  readonly latest: TestSend | null;
  /** Идёт POST — подпись ключа. */
  readonly posting: boolean;
  /** Отказ POST словами. */
  readonly error: string | null;
  /** Отправить (POST). Вызывает только удержание в диалоге. */
  readonly send: () => void;
  /** Забыть закрытую отправку: диалог снова предлагает отправить. */
  readonly forget: () => void;
}

export interface TestSendTrackerArgs {
  /**
   * Бренд ЭКРАНА (адреса) — уходит `expected_brand` и ни для чего больше (шапка файла): превью
   * идёт в тестовый канал бренда черновика, а сверка ловит экран, оставшийся без перехода.
   */
  readonly expectedBrand: string;
  readonly draftId: string;
  readonly versionNo: number;
  readonly contentHash: string;
  /** `test_sends` модели рендера — свежая первой. */
  readonly sends: readonly TestSend[];
  /** Отправка закрылась или экран устарел — перечитать модель рендера (мета-строка, статус). */
  readonly onSettled: () => void;
}

export function useTestSendTracker(a: TestSendTrackerArgs): TestSendTracker {
  const { expectedBrand, draftId, versionNo, contentHash, sends, onSettled } = a;
  const path = `/api/v1/drafts/${draftId}/versions/${String(versionNo)}`;
  const [tracked, setTracked] = useState<TestSend | null>(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Исходы, которые экран УЖЕ видел, — по id. Модель рендера перечитывается после исхода не
   * мгновенно, и до её ответа в `sends` та же отправка ещё «leased»: без этой памяти закрытый
   * диалог подхватил бы её снова, опрос пошёл бы по второму кругу, а мета-строка на миг
   * вернулась бы к «отправляется…» про уже отправленный пост.
   *
   * Память подменяет только НЕЗАКРЫТУЮ копию модели. Закрытая копия модели главнее памяти и
   * слежения: модель перечитывается после каждого исхода, который видел экран, поэтому её
   * закрытый снимок не старше их. И только она приносит то, что опрос увидеть уже не может:
   * поздний отчёт публикатора, заменивший уборочный `lease_expired` настоящим исходом, и исход,
   * записанный сервером после того, как опрос остановился на потолке 15 минут (ревью, ui-4).
   */
  const finished = useRef(new Map<string, TestSend>());

  // Отправки принадлежат ВЕРСИИ: другая версия — другой список, и чужая отправка на её
  // экране соврала бы о том, что уже проверено. Сброс во время рендера, а не в эффекте.
  const [forPath, setForPath] = useState(path);
  if (forPath !== path) {
    setForPath(path);
    setTracked(null);
    setPosting(false);
    setError(null);
    finished.current = new Map();
  }

  // Закрытая копия модели главнее памяти исходов (см. `finished`): память лишь держит исход,
  // до которого модель ещё не перечиталась.
  const head = sends[0] ?? null;
  const fromModel =
    head === null || isTerminal(head.state) ? head : (finished.current.get(head.id) ?? head);
  // Незакрытая отправка из модели (начата в другой вкладке или до перезагрузки) становится
  // отслеживаемой сама: иначе открытый диалог предложил бы вторую, а сервер вернул бы первую.
  if (tracked === null && fromModel !== null && !isTerminal(fromModel.state)) {
    setTracked(fromModel);
  }
  // Модель закрыла отслеживаемую отправку иначе, чем видел экран, или закрыла её после того,
  // как опрос перестал спрашивать, — слежение берёт её снимок. Сравнение по полям, а не по
  // ссылке: перечитанная модель — новые объекты с тем же содержимым.
  const modelCopy = tracked === null ? undefined : sends.find((t) => t.id === tracked.id);
  if (
    tracked !== null &&
    modelCopy !== undefined &&
    isTerminal(modelCopy.state) &&
    !sameSnapshot(modelCopy, tracked)
  ) {
    finished.current.set(modelCopy.id, modelCopy);
    setTracked(modelCopy);
  }

  const settledRef = useRef(onSettled);
  useEffect(() => {
    settledRef.current = onSettled;
  });
  // Версия, на которой экран стоит СЕЙЧАС: ответ POST, пришедший после перехода на другую
  // версию, принадлежит прежней и в слежение новой не попадает.
  const livePath = useRef(path);
  useEffect(() => {
    livePath.current = path;
  });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const trackedId = tracked?.id ?? null;
  const trackedAt = tracked?.created_at ?? null;
  const active = tracked !== null && !isTerminal(tracked.state);

  useEffect(() => {
    if (!active || trackedId === null || trackedAt === null) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let ac: AbortController | null = null;
    let stopped = false;
    const next = (): void => {
      // Дольше окна ключа и уборки не спрашиваем: такая отправка опросом уже не закроется.
      // Модель перечитывается один раз — вдруг сервер закрыл её, пока ответ терялся.
      if (nowMs() - isoToMs(trackedAt) > TEST_SEND_TRACK_MAX_MS) {
        settledRef.current();
        return;
      }
      timer = setTimeout(tick, TEST_SEND_POLL_MS);
    };
    const tick = (): void => {
      const mine = new AbortController();
      ac = mine;
      get<unknown>(path, mine.signal)
        .then((raw) => {
          if (stopped) return;
          const found = testSendsOf(raw).find((t) => t.id === trackedId);
          if (found !== undefined) {
            if (isTerminal(found.state)) finished.current.set(found.id, found);
            setTracked(found);
            if (isTerminal(found.state)) {
              settledRef.current();
              return;
            }
          }
          next();
        })
        // Сбой опроса молчит, как у полосы хода: отправку закроет исход или уборка сервера
        // (`leased` дольше 10 минут → `failed`), а красная плашка из-за одного GET учит её не
        // замечать.
        .catch(() => {
          if (stopped) return;
          next();
        });
    };
    next();
    return () => {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
      ac?.abort();
    };
  }, [active, path, trackedId, trackedAt]);

  const send = useCallback((): void => {
    if (posting) return;
    const at = path;
    setPosting(true);
    setError(null);
    const body: CreateTestSendBody = {
      expected_content_hash: contentHash,
      expected_brand: expectedBrand,
    };
    post<unknown>(`${at}/test-send`, body)
      .then((raw) => {
        if (!alive.current || livePath.current !== at) return;
        const t = testSendOfResponse(raw);
        if (t === null) {
          // Отправка записана, но ответ не разобрался: ход покажет перечитанная модель.
          settledRef.current();
          return;
        }
        if (isTerminal(t.state)) finished.current.set(t.id, t);
        setTracked(t);
        if (isTerminal(t.state)) settledRef.current();
      })
      .catch((e: unknown) => {
        if (!alive.current || livePath.current !== at) return;
        const f = testSendErrorText(e);
        setError(f.text);
        if (f.refresh) settledRef.current();
      })
      .finally(() => {
        if (alive.current && livePath.current === at) setPosting(false);
      });
  }, [expectedBrand, contentHash, path, posting]);

  const forget = useCallback((): void => {
    setError(null);
    setTracked((t) => (t !== null && isTerminal(t.state) ? null : t));
  }, []);

  return { tracked, latest: newerSend(fromModel, tracked), posting, error, send, forget };
}
